import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import PageArrival from '../components/PageArrival';
import { Shield as A1, DollarSign as A2, GraduationCap as A3, Map as A4, Award as A5, Briefcase as A6 } from 'lucide-react';

import { AppLayout, GlassCard, PageHeader } from '../components/Layout';
import PathFitDashboard from '../components/paths/PathFitDashboard';
import { Button } from '../components/ui/button';
import { 
  Target, Shield, Award, BookOpen, ExternalLink, Plus,
  Star, Clock, DollarSign, GraduationCap, Heart, Users,
  ChevronRight, CheckCircle, Plane, Ship, Anchor
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Military Branches Data
const BRANCHES = [
  {
    id: 'army',
    name: 'U.S. Army',
    icon: Shield,
    color: 'green',
    description: 'The largest branch, offering diverse career paths from infantry to cyber operations.',
    website: 'https://www.goarmy.com',
    careers: ['Infantry', 'Military Police', 'Cyber Operations', 'Medical Corps', 'Aviation', 'Engineering'],
    benefits: ['Tuition assistance up to $250k', 'Housing allowance', 'Healthcare', '30 days paid vacation'],
    requirements: ['17-35 years old', 'U.S. citizen or permanent resident', 'High school diploma or GED', 'Pass ASVAB test'],
  },
  {
    id: 'navy',
    name: 'U.S. Navy',
    icon: Anchor,
    color: 'blue',
    description: 'Maritime operations worldwide, from submarines to aircraft carriers.',
    website: 'https://www.navy.com',
    careers: ['Naval Aviation', 'Nuclear Engineering', 'Navy SEALs', 'Hospital Corpsman', 'Intelligence', 'Cryptology'],
    benefits: ['Nuclear training worth $100k+', 'World travel', 'Technical certifications', 'Leadership training'],
    requirements: ['17-39 years old', 'U.S. citizen', 'High school diploma', 'Pass ASVAB test'],
  },
  {
    id: 'airforce',
    name: 'U.S. Air Force',
    icon: Plane,
    color: 'cyan',
    description: 'Air and space superiority with cutting-edge technology careers.',
    website: 'https://www.airforce.com',
    careers: ['Pilot', 'Space Operations', 'Cyber Security', 'Air Traffic Control', 'Intelligence', 'Medical'],
    benefits: ['Highest tech training', 'Best quality of life', 'Space Force opportunities', 'Excellent facilities'],
    requirements: ['17-39 years old', 'U.S. citizen', 'High school diploma', 'Pass ASVAB test'],
  },
  {
    id: 'marines',
    name: 'U.S. Marine Corps',
    icon: Target,
    color: 'red',
    description: 'Elite expeditionary force known for discipline and rapid response.',
    website: 'https://www.marines.com',
    careers: ['Infantry', 'Aviation', 'Cyber', 'Intelligence', 'Logistics', 'Communications'],
    benefits: ['Elite training', 'Strong brotherhood', 'Leadership development', 'Respected worldwide'],
    requirements: ['17-28 years old', 'U.S. citizen', 'High school diploma', 'Pass ASVAB test'],
  },
  {
    id: 'coastguard',
    name: 'U.S. Coast Guard',
    icon: Ship,
    color: 'orange',
    description: 'Maritime law enforcement, search and rescue, environmental protection.',
    website: 'https://www.gocoastguard.com',
    careers: ['Maritime Law Enforcement', 'Search & Rescue', 'Marine Science', 'Aviation', 'Cyber', 'Intelligence'],
    benefits: ['Stationed in U.S.', 'Work-life balance', 'Meaningful missions', 'Technical training'],
    requirements: ['17-31 years old', 'U.S. citizen', 'High school diploma', 'Pass ASVAB test'],
  },
  {
    id: 'spaceforce',
    name: 'U.S. Space Force',
    icon: Star,
    color: 'purple',
    description: 'Newest branch focused on space operations and satellite systems.',
    website: 'https://www.spaceforce.mil',
    careers: ['Space Operations', 'Cyber Operations', 'Intelligence', 'Engineering', 'Acquisitions'],
    benefits: ['Cutting-edge technology', 'Growing branch', 'High-demand skills', 'Space industry connections'],
    requirements: ['17-39 years old', 'U.S. citizen', 'High school diploma', 'Pass ASVAB test'],
  },
];

// ASVAB Info
const ASVAB_INFO = {
  name: 'Armed Services Vocational Aptitude Battery',
  description: 'A multiple-choice test that determines qualification for enlistment and helps identify the best military occupational specialties.',
  sections: [
    { name: 'General Science', abbr: 'GS', description: 'Physical and biological sciences' },
    { name: 'Arithmetic Reasoning', abbr: 'AR', description: 'Word problems requiring math' },
    { name: 'Word Knowledge', abbr: 'WK', description: 'Vocabulary and word meanings' },
    { name: 'Paragraph Comprehension', abbr: 'PC', description: 'Reading comprehension' },
    { name: 'Mathematics Knowledge', abbr: 'MK', description: 'High school math concepts' },
    { name: 'Electronics Information', abbr: 'EI', description: 'Electrical concepts and electronics' },
    { name: 'Auto & Shop Information', abbr: 'AS', description: 'Automotive and shop practices' },
    { name: 'Mechanical Comprehension', abbr: 'MC', description: 'Mechanical and physical principles' },
    { name: 'Assembling Objects', abbr: 'AO', description: 'Spatial relationships' },
  ],
  minimumScores: {
    army: 31,
    navy: 35,
    airforce: 36,
    marines: 32,
    coastguard: 40,
    spaceforce: 36,
  },
  resources: [
    { name: 'Official ASVAB Practice Test', url: 'https://www.officialasvab.com/applicants/asvab-practice-test/', type: 'Free Practice' },
    { name: 'ASVAB for Dummies', url: 'https://www.amazon.com/ASVAB-Dummies-Rod-Powers/dp/1119684285', type: 'Book' },
    { name: 'Kaplan ASVAB Prep Plus', url: 'https://www.amazon.com/ASVAB-Prep-Plus-2022-2023-Strategies/dp/1506277802', type: 'Book' },
    { name: 'March2Success', url: 'https://www.march2success.com', type: 'Free Course' },
    { name: 'ASVAB Boot Camp', url: 'https://www.asvabbootcamp.com', type: 'Online Course' },
  ],
};

export default function MilitaryPathPage() {
  const [arrived, setArrived] = useState(false);
  const { user, getAuthHeader } = useAuth();
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [activeTab, setActiveTab] = useState('branches');

  const addToGoals = async (item, category = 'career') => {
    try {
      await axios.post(`${API}/goals`, {
        title: item.title || item.name || item,
        description: item.description || `Added from Military Career Path`,
        category: category,
        priority: 'medium',
        status: 'backlog',
        source: 'Military Path'
      }, {
        ...getAuthHeader(),
        withCredentials: true
      });
      toast.success('Added to your Goals! 🎯');
    } catch (error) {
      toast.error('Failed to add to goals');
    }
  };

  if (!arrived) return (
    <AppLayout>
      <PageArrival
        testid="military-arrival"
        eyebrow="Military Pathways"
        title="Service is a career, and a springboard."
        subtitle="Whether you're considering enlisting, serving now, or getting out, this is what each branch actually offers, what your job translates to in civilian pay, and every education benefit you've earned and can still use."
        accent="#34d399"
        second="#60a5fa"
        ctaLabel="Open military pathways"
        narration={"This is Military Pathways. If you're considering service, you'll see what each branch offers, what the jobs are, and what the benefits are worth in real money. If you're already serving or getting out, this is where your experience gets translated: your job code into civilian occupations, your leadership into language hiring managers use, and your benefits into a funded plan. Nothing here is a recruiting pitch, it's the numbers and the routes."}
        facts={[
          { icon: A2, head: 'Benefits in real dollars', sub: 'GI Bill, tuition assistance, housing allowance' },
          { icon: A6, head: 'Your job, translated', sub: 'Military occupation to civilian roles and pay' },
          { icon: A5, head: 'Credentials you keep', sub: 'Licences and certifications that transfer out' },
        ]}
        modulesTitle="What you'll find here"
        modules={[
          { icon: A1, label: 'Branch by branch', line: 'What each one does, what it asks, and what daily life looks like.', hue: '#34d399' },
          { icon: A6, label: 'Jobs and MOS', line: 'The roles available and what they become on the outside.', hue: '#22d3ee' },
          { icon: A3, label: 'Education benefits', line: 'Post-9/11 GI Bill, VR&E, tuition assistance, and how to stack them.', hue: '#a78bfa' },
          { icon: A4, label: 'Transition plan', line: 'The timeline out: paperwork, skills, résumé, network, first offer.', hue: '#60a5fa' },
          { icon: A5, label: 'Credential transfer', line: 'What your training already qualifies you for in the civilian market.', hue: '#fbbf24' },
          { icon: A2, label: 'Pay reality', line: 'Service pay against civilian medians for the same work.', hue: '#f472b6' },
        ]}
        onStart={() => { setArrived(true); window.scrollTo({ top: 0 }); }}
      />
    </AppLayout>
  );

  return (
    <AppLayout>
      <PageHeader 
        title="Military Career Path"
        description="Serve, earn, and finish debt-free, 6 branches, 150+ specialties, full healthcare from day one."
      />

      {/* Fit dashboard, is a military pathway a good match? */}
      <PathFitDashboard
        pathLabel="a military pathway"
        fit={{ overall: 62, career: 58, financial: 95, timeline: 80 }}
        reasons={['Full healthcare from day one', '$300k+ GI Bill lifetime benefit', '150+ specialties to match your interests', 'Structured progression']}
      />

      {/* Quick Stats, brand cyan/violet */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <GlassCard className="text-center">
          <Shield className="w-6 h-6 mx-auto mb-2 text-cyan-300" />
          <p className="text-2xl font-bold text-white">6</p>
          <p className="text-xs text-slate-300">Military branches</p>
        </GlassCard>
        <GlassCard className="text-center">
          <DollarSign className="w-6 h-6 mx-auto mb-2 text-violet-300" />
          <p className="text-2xl font-bold text-white">$300k+</p>
          <p className="text-xs text-slate-300">GI Bill lifetime benefit</p>
        </GlassCard>
        <GlassCard className="text-center">
          <Award className="w-6 h-6 mx-auto mb-2 text-sky-300" />
          <p className="text-2xl font-bold text-white">150+</p>
          <p className="text-xs text-slate-300">Career specialties</p>
        </GlassCard>
        <GlassCard className="text-center">
          <Heart className="w-6 h-6 mx-auto mb-2 text-pink-300" />
          <p className="text-2xl font-bold text-white">100%</p>
          <p className="text-xs text-slate-300">Healthcare & housing</p>
        </GlassCard>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {[
          { id: 'branches', label: 'Military Branches', icon: Shield },
          { id: 'asvab', label: 'ASVAB Test', icon: BookOpen },
          { id: 'benefits', label: 'Benefits', icon: Award },
          { id: 'process', label: 'Enlistment Process', icon: CheckCircle },
        ].map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-full text-sm border flex items-center gap-2 whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? 'border-cyan-300/60 bg-cyan-300/15 text-white'
                  : 'border-white/12 text-slate-300 hover:text-white'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Branches Tab */}
      {activeTab === 'branches' && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {BRANCHES.map(branch => {
            const Icon = branch.icon;
            return (
              <GlassCard 
                key={branch.id}
                className={`cursor-pointer transition-all hover:scale-[1.02] ${
                  selectedBranch?.id === branch.id ? 'ring-2 ring-blue-500' : ''
                }`}
                onClick={() => setSelectedBranch(selectedBranch?.id === branch.id ? null : branch)}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-12 h-12 bg-${branch.color}-500/20 rounded-xl flex items-center justify-center`}>
                    <Icon className={`w-6 h-6 text-${branch.color}-400`} />
                  </div>
                  <a 
                    href={branch.website} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="text-blue-400 hover:text-blue-300"
                  >
                    <ExternalLink className="w-5 h-5" />
                  </a>
                </div>
                
                <h3 className="text-xl font-bold text-white mb-2">{branch.name}</h3>
                <p className="text-slate-300 text-sm mb-4">{branch.description}</p>
                
                {selectedBranch?.id === branch.id && (
                  <div className="space-y-4 pt-4 border-t border-navy-700">
                    <div>
                      <h4 className="text-sm font-semibold text-slate-300 mb-2">Popular Careers:</h4>
                      <div className="flex flex-wrap gap-2">
                        {branch.careers.map(career => (
                          <span key={career} className="px-2 py-1 bg-navy-700 text-slate-300 text-xs rounded-full">
                            {career}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="text-sm font-semibold text-slate-300 mb-2">Requirements:</h4>
                      <ul className="text-sm text-slate-300 space-y-1">
                        {branch.requirements.map((req, i) => (
                          <li key={i} className="flex items-center gap-2">
                            <CheckCircle className="w-3 h-3 text-green-400" />
                            {req}
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          addToGoals({ name: `Research ${branch.name}`, description: `Learn more about careers in the ${branch.name}` });
                        }}
                        className="flex-1 bg-blue-600 hover:bg-blue-700"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add to Goals
                      </Button>
                      <a 
                        href={branch.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="flex-1"
                      >
                        <Button size="sm" variant="outline" className="w-full border-navy-600">
                          Visit Website
                        </Button>
                      </a>
                    </div>
                  </div>
                )}
              </GlassCard>
            );
          })}
        </div>
      )}

      {/* ASVAB Tab */}
      {activeTab === 'asvab' && (
        <div className="space-y-6">
          <GlassCard>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-xl font-bold text-white">{ASVAB_INFO.name}</h3>
                <p className="text-slate-300 mt-1">{ASVAB_INFO.description}</p>
              </div>
              <Button
                onClick={() => addToGoals({ name: 'Study for ASVAB Test', description: 'Prepare and score well on the ASVAB' }, 'certification')}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add to Goals
              </Button>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6 mt-6">
              <div>
                <h4 className="font-semibold text-white mb-3">Test Sections</h4>
                <div className="space-y-2">
                  {ASVAB_INFO.sections.map(section => (
                    <div key={section.abbr} className="flex items-center justify-between p-3 bg-navy-900/50 rounded-lg">
                      <div>
                        <span className="text-white font-medium">{section.name}</span>
                        <span className="text-slate-300 text-sm ml-2">({section.abbr})</span>
                      </div>
                      <span className="text-slate-300 text-sm">{section.description}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div>
                <h4 className="font-semibold text-white mb-3">Minimum Scores by Branch</h4>
                <div className="space-y-2 mb-6">
                  {Object.entries(ASVAB_INFO.minimumScores).map(([branch, score]) => {
                    const branchData = BRANCHES.find(b => b.id === branch);
                    return (
                      <div key={branch} className="flex items-center justify-between p-3 bg-navy-900/50 rounded-lg">
                        <span className="text-white capitalize">{branchData?.name || branch}</span>
                        <span className={`font-bold ${score >= 36 ? 'text-amber-400' : 'text-green-400'}`}>
                          {score} AFQT
                        </span>
                      </div>
                    );
                  })}
                </div>
                
                <h4 className="font-semibold text-white mb-3">Study Resources</h4>
                <div className="space-y-2">
                  {ASVAB_INFO.resources.map(resource => (
                    <a
                      key={resource.name}
                      href={resource.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between p-3 bg-navy-900/50 rounded-lg hover:bg-navy-800 transition-colors"
                    >
                      <div>
                        <span className="text-white">{resource.name}</span>
                        <span className="text-slate-300 text-sm ml-2">({resource.type})</span>
                      </div>
                      <ExternalLink className="w-4 h-4 text-blue-400" />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </GlassCard>
        </div>
      )}

      {/* Benefits Tab */}
      {activeTab === 'benefits' && (
        <div className="grid md:grid-cols-2 gap-6">
          <GlassCard>
            <GraduationCap className="w-10 h-10 text-purple-400 mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Education Benefits</h3>
            <ul className="space-y-3 text-slate-300">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                <div>
                  <strong>GI Bill:</strong> Up to $250,000 for college tuition, housing, and books
                </div>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                <div>
                  <strong>Tuition Assistance:</strong> $4,500/year while serving
                </div>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                <div>
                  <strong>Student Loan Repayment:</strong> Up to $65,000 in loan forgiveness
                </div>
              </li>
            </ul>
          </GlassCard>
          
          <GlassCard>
            <Heart className="w-10 h-10 text-red-400 mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Healthcare</h3>
            <ul className="space-y-3 text-slate-300">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                <div>
                  <strong>TRICARE:</strong> Full medical, dental, and vision coverage
                </div>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                <div>
                  <strong>Family Coverage:</strong> Spouse and children included
                </div>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                <div>
                  <strong>Mental Health:</strong> Counseling and support services
                </div>
              </li>
            </ul>
          </GlassCard>
          
          <GlassCard>
            <DollarSign className="w-10 h-10 text-emerald-400 mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Financial Benefits</h3>
            <ul className="space-y-3 text-slate-300">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                <div>
                  <strong>Base Pay:</strong> $24,000 - $150,000+ based on rank
                </div>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                <div>
                  <strong>Housing Allowance:</strong> BAH covers rent in most areas
                </div>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                <div>
                  <strong>Retirement:</strong> Pension after 20 years of service
                </div>
              </li>
            </ul>
          </GlassCard>
          
          <GlassCard>
            <Users className="w-10 h-10 text-blue-400 mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Career Development</h3>
            <ul className="space-y-3 text-slate-300">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                <div>
                  <strong>Technical Training:</strong> Industry-recognized certifications
                </div>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                <div>
                  <strong>Leadership:</strong> Management experience at young age
                </div>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                <div>
                  <strong>Security Clearance:</strong> Opens doors in government/defense
                </div>
              </li>
            </ul>
          </GlassCard>
        </div>
      )}

      {/* Process Tab */}
      {activeTab === 'process' && (
        <GlassCard>
          <h3 className="text-xl font-bold text-white mb-6">Enlistment Process</h3>
          <div className="space-y-6">
            {[
              { step: 1, title: 'Research & Decide', desc: 'Explore branches and career options that match your interests', time: '1-2 weeks' },
              { step: 2, title: 'Contact a Recruiter', desc: 'Visit a local recruiting office or call to schedule a meeting', time: '1 day' },
              { step: 3, title: 'Take the ASVAB', desc: 'Complete the aptitude test to determine eligible jobs', time: '3 hours' },
              { step: 4, title: 'Physical Examination', desc: 'Complete medical exam at MEPS (Military Entrance Processing)', time: '1-2 days' },
              { step: 5, title: 'Select Your Job', desc: 'Choose your Military Occupational Specialty (MOS) based on ASVAB scores', time: '1 day' },
              { step: 6, title: 'Sign Contract', desc: 'Review and sign your enlistment contract', time: '1 day' },
              { step: 7, title: 'Ship to Basic Training', desc: 'Begin your military career with basic training', time: '8-13 weeks' },
            ].map((item, index) => (
              <div key={item.step} className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold">
                    {item.step}
                  </div>
                  {index < 6 && <div className="w-0.5 h-full bg-blue-600/30 mt-2" />}
                </div>
                <div className="flex-1 pb-6">
                  <div className="flex items-center justify-between">
                    <h4 className="text-white font-semibold">{item.title}</h4>
                    <span className="text-slate-300 text-sm flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {item.time}
                    </span>
                  </div>
                  <p className="text-slate-300 mt-1">{item.desc}</p>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => addToGoals({ name: item.title, description: item.desc })}
                    className="mt-2 text-blue-400 hover:text-blue-300 p-0 h-auto"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add to Goals
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </GlassCard>
      )}
    </AppLayout>
  );
}
